import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { ExtraDataItem } from "../../hooks/use-citation"
import type { CategorizedCitation } from "../../utils/citation-categorizer"
import type { DocumentReferencePreview } from "@/api/models"
import type { TFunction } from "i18next"

import { getCitationFilePath } from "@/api/api"
import { FileText } from "@/components/icons"
import { parseCitationParts } from "@/utils/citation-utils"

import { DocumentCitationItem } from "../document-citation-item"

type UserDocumentsSectionProps = {
  documents: CategorizedCitation[]
  documentReferences?: DocumentReferencePreview[]
  citationSection: string
  formatCitationDisplay: (citation: string) => {
    fileNameWithPage: string
    fileNameWithoutPage: string
    pageNumber: number | null
  }
  handleDownloadClicked: (citation: string) => void
  setExtraData: (data: ExtraDataItem[] | null) => void
  setOpenMetadataDialog: (open: boolean) => void
  openPreview: (citation: string, citationSection: string, options?: { chunkText?: string }) => void
  t: TFunction
  highlightedIndex: number | null
  messageIndex?: number
  onNavigateToInline: (index: number) => void
}

export function UserDocumentsSection({
  documents,
  documentReferences,
  citationSection,
  formatCitationDisplay,
  handleDownloadClicked,
  setExtraData,
  setOpenMetadataDialog,
  openPreview,
  t,
  highlightedIndex,
  messageIndex,
  onNavigateToInline,
}: UserDocumentsSectionProps) {
  const { t: translate } = useTranslation()

  const titlesByDocumentId = useMemo(() => {
    const entries = (documentReferences ?? []).map(
      (reference) => [reference.document_id, reference.title] as const
    )
    return new Map(entries)
  }, [documentReferences])

  const formatUserDocumentDisplay = (citation: string) => {
    const fallback = formatCitationDisplay(citation)
    const parsed = parseCitationParts(citation)
    if (!parsed.source.startsWith("doc:")) {
      return fallback
    }

    const documentId = parsed.source.slice(4)
    const pageNumber = parsed.page ?? null
    const title = titlesByDocumentId.get(documentId) || documentId
    const fileNameWithoutPage = `${translate("answer.user_documents", { defaultValue: "Documents" })}: ${title}`
    const fileNameWithPage = pageNumber
      ? `${fileNameWithoutPage}#page=${pageNumber}`
      : fileNameWithoutPage
    return { fileNameWithPage, fileNameWithoutPage, pageNumber }
  }

  if (documents.length === 0) return null

  return (
    <div className="space-y-2">
      <h4 className="text-muted-foreground flex items-center gap-2 text-sm font-semibold">
        <FileText className="size-4" />
        <span>
          {translate("answer.user_documents", { defaultValue: "Documents" })} ({documents.length})
        </span>
      </h4>
      <div className="grid grid-cols-1 gap-2">
        {documents.map(({ citation, index }) => {
          const pathWithPage = getCitationFilePath(citation, citationSection)
          return (
            <DocumentCitationItem
              key={index}
              disableMetadata
              citation={citation}
              citationSection={citationSection}
              extraData={null}
              formatCitationDisplay={formatUserDocumentDisplay}
              handleDownloadClicked={handleDownloadClicked}
              index={index}
              isHighlighted={highlightedIndex === index}
              messageIndex={messageIndex}
              pathWithPage={pathWithPage}
              setExtraData={setExtraData}
              setOpenMetadataDialog={setOpenMetadataDialog}
              t={t}
              onNavigateToInline={onNavigateToInline}
              onViewDocument={openPreview}
            />
          )
        })}
      </div>
    </div>
  )
}
